// Mock Data Store - Shared data for development and testing
import type {
  AccessDevice,
  AccessLog,
  Amenity,
  AmenityReservation,
  Announcement,
  Category,
  CommunityDocument,
  Expense,
  FinanceMovement,
  Guest,
  LicensePlate,
  Section,
  SystemAuditLog,
  SystemSettings,
  Ticket,
  Unit,
  User
} from '@yawi/types';

// Re-import enums for runtime use
import {
  AccessStatus as AccessStatusEnum,
  AccessType as AccessTypeEnum,
  AnnouncementType as AnnouncementTypeEnum,
  AuditAction as AuditActionEnum,
  DeviceStatus as DeviceStatusEnum,
  DeviceType as DeviceTypeEnum,
  DocumentCategory as DocumentCategoryEnum,
  ExpenseCategory as ExpenseCategoryEnum,
  PlateStatus as PlateStatusEnum,
  ReservationStatus as ReservationStatusEnum,
  SectionStatus as SectionStatusEnum,
  TicketCategory as TicketCategoryEnum,
  TicketStatus as TicketStatusEnum,
  UnitType as UnitTypeEnum,
  UserRole as UserRoleEnum,
  VaccineType
} from '@yawi/types';

export const MOCK_SECTIONS: Section[] = [
  {
    id: 'sec1',
    name: 'Torre A',
    code: 'TORRE-A',
    description: 'Torre principal',
    status: SectionStatusEnum.ACTIVE,
    unitsCount: 120,
    residentsCount: 235,
    assignedMembersCount: 1,
    createdAt: '2026-01-01T10:00:00.000Z',
    updatedAt: '2026-01-01T10:00:00.000Z',
  },
  {
    id: 'sec2',
    name: 'Torre B',
    code: 'TORRE-B',
    description: 'Torre secundaria',
    status: SectionStatusEnum.ACTIVE,
    unitsCount: 96,
    residentsCount: 181,
    assignedMembersCount: 1,
    createdAt: '2026-01-01T10:00:00.000Z',
    updatedAt: '2026-01-01T10:00:00.000Z',
  },
  {
    id: 'sec3',
    name: 'Área Comercial',
    code: 'COMERCIAL',
    description: 'Locales comerciales',
    status: SectionStatusEnum.ACTIVE,
    unitsCount: 12,
    residentsCount: 0,
    assignedMembersCount: 0,
    createdAt: '2026-01-01T10:00:00.000Z',
    updatedAt: '2026-01-01T10:00:00.000Z',
  },
];

export const MOCK_USERS_LIST: User[] = [
  {
    id: 'u1',
    name: 'Roberto Gómez',
    email: 'residente@demo.yawi.com',
    role: UserRoleEnum.RESIDENT,
    pin: 'password123',
    status: 'ACTIVE',
    avatarUrl: 'https://i.pravatar.cc/150?u=u1',
    residenceInfo: { tower: 'A', unit: '402', street: 'Av. Principal' },
    units: [{
      id: 'uu1',
      unitId: 'unit1',
      isOwner: true,
      isPrimary: true,
      unit: { id: 'unit1', type: UnitTypeEnum.APARTMENT, name: 'A-402', tower: 'A', number: '402', floor: '4' },
    }],
    phone: '5512345678',
    fiscalData: {
      rfc: 'GOMR850101ABC',
      businessName: 'Roberto Gómez SA',
      taxRegime: '612',
      zipCode: '11560',
      address: 'Av. Principal 123',
      email: 'facturas@roberto.com',
    },
    householdMembers: [
      { id: 'hm1', name: 'María Gómez', relation: 'SPOUSE', age: 35, email: 'maria@email.com', accountStatus: 'ACTIVE' },
      { id: 'hm2', name: 'Tomas Gómez', relation: 'CHILD', age: 8, email: 'tomas@email.com', accountStatus: 'PENDING_VERIFICATION' },
      { id: 'hm3', name: 'Juana García', relation: 'OTHER', phone: '5500000000', email: 'juana@email.com', accountStatus: 'INACTIVE' },
    ],
    pets: [
      {
        id: 'p1',
        name: 'Max',
        type: 'DOG',
        breed: 'Golden Retriever',
        color: 'Dorado',
        age: 3,
        birthDate: '2021-03-15',
        photoUrl: 'https://images.unsplash.com/photo-1552053831-71594a27632d?auto=format&fit=crop&q=80',
        vaccinations: [
          { id: 'v1', petId: 'p1', vaccineType: VaccineType.RABIES, appliedAt: '2023-05-15', nextDoseAt: '2024-05-15', veterinarian: 'Dr. García' },
          { id: 'v2', petId: 'p1', vaccineType: VaccineType.PARVOVIRUS, appliedAt: '2023-06-20', nextDoseAt: '2024-06-20' },
        ],
      },
    ],
  },
  {
    id: 'u2',
    name: 'Admin Principal',
    email: 'admin@demo.yawi.com',
    role: UserRoleEnum.ADMIN,
    tenantRole: 'ADMIN',
    memberId: 'm2',
    pin: 'password123',
    status: 'ACTIVE',
    avatarUrl: 'https://i.pravatar.cc/150?u=admin',
    phone: '5599999999',
  },
  {
    id: 'u3',
    name: 'Guardia Entrada',
    email: 'guardia@resiguard.com',
    role: UserRoleEnum.GUARD,
    tenantRole: 'GUARD',
    memberId: 'm3',
    pin: '9999',
    status: 'ACTIVE',
    avatarUrl: 'https://i.pravatar.cc/150?u=guard',
    phone: '5588888888',
  },
  {
    id: 'u4',
    name: 'Ana Martínez',
    email: 'ana@vecinos.com',
    role: UserRoleEnum.RESIDENT,
    pin: '1011',
    status: 'ACTIVE',
    avatarUrl: 'https://i.pravatar.cc/150?u=ana',
    residenceInfo: { tower: 'B', unit: '101' },
    units: [{
      id: 'uu4',
      unitId: 'unit2',
      isOwner: false,
      isPrimary: true,
      unit: { id: 'unit2', type: UnitTypeEnum.APARTMENT, name: 'B-101', tower: 'B', number: '101', floor: '1' },
    }],
    phone: '5577777777',
  },
  {
    id: 'u5',
    name: 'Carlos Gomez',
    email: 'carlos@vecinos.com',
    role: UserRoleEnum.RESIDENT,
    pin: '2020',
    status: 'ACTIVE',
    avatarUrl: 'https://i.pravatar.cc/150?u=carlos',
    residenceInfo: { tower: 'A', unit: '402', street: 'Av. Principal' },
    units: [{
      id: 'uu5',
      unitId: 'unit1',
      isOwner: false,
      isPrimary: true,
      unit: { id: 'unit1', type: UnitTypeEnum.APARTMENT, name: 'A-402', tower: 'A', number: '402', floor: '4' },
    }],
    phone: '5566666666',
  },
];

export const CURRENT_USER: User = MOCK_USERS_LIST[0];

export const MOCK_UNITS: Unit[] = [
  { id: 'unit1', type: UnitTypeEnum.APARTMENT, name: 'A-402', tower: 'A', number: '402', floor: '4', sectionId: 'sec1' },
  { id: 'unit2', type: UnitTypeEnum.APARTMENT, name: 'B-101', tower: 'B', number: '101', floor: '1', sectionId: 'sec2' },
  { id: 'unit3', type: UnitTypeEnum.HOUSE, name: 'Calle Roble 12', street: 'Calle Roble', exteriorNumber: '12', block: 'C', sectionId: 'sec3' },
];

export const MOCK_GUESTS: Guest[] = [
  {
    id: 'g1',
    name: 'Juan Mecánico',
    hostId: 'u1',
    type: 'SERVICE',
    validFrom: new Date().toISOString(),
    validTo: new Date(Date.now() + 86400000).toISOString(),
    accessCode: 'QR-MECANICO',
    numericCode: '123456',
    plateNumber: 'ABC-123',
  },
  {
    id: 'g2',
    name: 'Fiesta Sábado',
    hostId: 'u1',
    type: 'PARTY',
    validFrom: new Date(Date.now() + 172800000).toISOString(),
    validTo: new Date(Date.now() + 190000000).toISOString(),
    accessCode: 'QR-FIESTA',
    numericCode: '654321',
    notes: '20 personas max',
  },
];

export const MOCK_LOGS: AccessLog[] = [
  {
    id: 'l1',
    timestamp: new Date().toISOString(),
    userName: 'Roberto Gómez',
    userId: 'u1',
    method: AccessTypeEnum.QR_RESIDENT,
    entryPoint: 'Acceso Principal',
    status: AccessStatusEnum.GRANTED,
    photos: ['https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop'],
  },
  {
    id: 'l2',
    timestamp: new Date(Date.now() - 3600000).toISOString(),
    userName: 'Juan Mecánico',
    method: AccessTypeEnum.QR_GUEST,
    entryPoint: 'Acceso Proveedores',
    status: AccessStatusEnum.GRANTED,
    plateNumber: 'ABC-123',
    photos: [
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop',
      'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=300&fit=crop',
    ],
  },
  {
    id: 'l3',
    timestamp: new Date(Date.now() - 7200000).toISOString(),
    userName: 'Desconocido',
    method: AccessTypeEnum.MANUAL,
    entryPoint: 'Lobby A',
    status: AccessStatusEnum.DENIED,
    details: 'Código inválido',
    photos: ['https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop'],
  },
  {
    id: 'l4',
    timestamp: new Date(Date.now() - 86400000).toISOString(),
    userName: 'María López',
    userId: 'u1',
    method: AccessTypeEnum.QR_RESIDENT,
    entryPoint: 'Acceso Principal',
    status: AccessStatusEnum.GRANTED,
    details: 'Visita familiar',
    photos: ['https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop'],
  },
  {
    id: 'l5',
    timestamp: new Date(Date.now() - 172800000).toISOString(),
    userName: 'Paquetería DHL',
    method: AccessTypeEnum.QR_GUEST,
    entryPoint: 'Acceso Proveedores',
    status: AccessStatusEnum.GRANTED,
    photos: ['https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop'],
  },
];

export const MOCK_AMENITIES: Amenity[] = [
  {
    id: 'am1',
    name: 'Salón de Usos Múltiples',
    capacity: 50,
    imageUrl: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&q=80',
    requiresDeposit: true,
    depositAmount: 2000,
    costPerHour: 500,
    minHours: 3,
    maxHours: 8,
    rules: 'No fumar. Limpiar antes de salir.',
    isActive: true,
    requiresApproval: true,
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: '2024-01-01T00:00:00.000Z',
  },
  {
    id: 'am2',
    name: 'Cancha de Padel',
    capacity: 4,
    imageUrl: 'https://images.unsplash.com/photo-1554068865-248cecd4883b?auto=format&fit=crop&q=80',
    requiresDeposit: false,
    costPerHour: 0,
    minHours: 1,
    maxHours: 2,
    rules: 'Usar calzado adecuado.',
    isActive: true,
    requiresApproval: false,
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: '2024-01-01T00:00:00.000Z',
  },
  {
    id: 'am3',
    name: 'Asadores Roof Garden',
    capacity: 15,
    imageUrl: 'https://images.unsplash.com/photo-1555529733-0e670560f7e1?auto=format&fit=crop&q=80',
    requiresDeposit: true,
    depositAmount: 500,
    costPerHour: 100,
    minHours: 2,
    maxHours: 5,
    rules: 'Apagar carbón al terminar.',
    isActive: true,
    requiresApproval: false,
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: '2024-01-01T00:00:00.000Z',
  },
];

export const MOCK_RESERVATIONS: AmenityReservation[] = [
  {
    id: 'r1',
    memberId: 'u1',
    member: { id: 'u1', displayName: 'Roberto Gómez' },
    amenityId: 'am2',
    date: new Date().toISOString().split('T')[0],
    startTime: '10:00',
    hours: 1,
    totalCost: 0,
    status: ReservationStatusEnum.CONFIRMED,
    createdAt: new Date().toISOString(),
    depositAmount: 0,
    isRefunded: false,
  },
  {
    id: 'r2',
    memberId: 'u1',
    member: { id: 'u1', displayName: 'Roberto Gómez' },
    amenityId: 'am1',
    date: new Date(Date.now() - 86400000).toISOString().split('T')[0],
    startTime: '18:00',
    hours: 4,
    totalCost: 2000,
    status: ReservationStatusEnum.CONFIRMED,
    createdAt: new Date().toISOString(),
    depositAmount: 2000,
    isRefunded: false,
  },
];

export const MOCK_CATEGORIES: Category[] = [
  { id: 'cat-maintenance', name: 'Mantenimiento', kind: 'EXPENSE', color: '#7F77DD', icon: 'wrench', isSystem: true, isActive: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'cat-utilities', name: 'Servicios (CFE, agua, gas)', kind: 'EXPENSE', color: '#4FA3D1', icon: 'zap', isSystem: true, isActive: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'cat-salaries', name: 'Sueldos', kind: 'EXPENSE', color: '#E5B800', icon: 'users', isSystem: true, isActive: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'cat-cleaning', name: 'Limpieza', kind: 'EXPENSE', color: '#639922', icon: 'sparkles', isSystem: true, isActive: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'cat-security', name: 'Seguridad', kind: 'EXPENSE', color: '#E24B4A', icon: 'shield', isSystem: true, isActive: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'cat-insurance', name: 'Seguros', kind: 'EXPENSE', color: '#9C6ADE', icon: 'file-text', isSystem: true, isActive: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'cat-services', name: 'Servicios generales', kind: 'EXPENSE', color: '#4FA3D1', icon: 'settings', isSystem: true, isActive: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'cat-supplies', name: 'Insumos', kind: 'EXPENSE', color: '#EF9F27', icon: 'package', isSystem: true, isActive: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'cat-other', name: 'Otro', kind: 'EXPENSE', color: '#888780', icon: 'tag', isSystem: true, isActive: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
];

function daysAgo(n: number): string {
  return new Date(Date.now() - n * 86400000).toISOString().split('T')[0];
}

export const MOCK_EXPENSES: Expense[] = [
  {
    id: 'exp1',
    title: 'Mantenimiento Elevadores',
    amount: 15000,
    category: ExpenseCategoryEnum.MAINTENANCE,
    categoryId: 'cat-maintenance',
    categoryName: 'Mantenimiento',
    categoryColor: '#7F77DD',
    date: daysAgo(0),
    provider: 'Elevadores Otis',
    status: 'PAID',
    invoiceUrl: '#',
  },
  {
    id: 'exp2',
    title: 'Recibo CFE Áreas Comunes',
    amount: 8500,
    category: ExpenseCategoryEnum.SERVICES,
    categoryId: 'cat-utilities',
    categoryName: 'Servicios (CFE, agua, gas)',
    categoryColor: '#4FA3D1',
    date: daysAgo(1),
    provider: 'CFE',
    status: 'PAID',
  },
  {
    id: 'exp3',
    title: 'Nómina personal de limpieza',
    amount: 22000,
    category: ExpenseCategoryEnum.SALARIES,
    categoryId: 'cat-salaries',
    categoryName: 'Sueldos',
    categoryColor: '#E5B800',
    date: daysAgo(3),
    provider: 'Nómina interna',
    status: 'PAID',
  },
  {
    id: 'exp4',
    title: 'Ronda de vigilancia extra (evento)',
    amount: 4200,
    category: ExpenseCategoryEnum.SECURITY,
    categoryId: 'cat-security',
    categoryName: 'Seguridad',
    categoryColor: '#E24B4A',
    date: daysAgo(5),
    provider: 'Seguridad Alfa',
    status: 'PENDING',
  },
  {
    id: 'exp5',
    title: 'Insumos de limpieza mensuales',
    amount: 3100,
    category: ExpenseCategoryEnum.SUPPLIES,
    categoryId: 'cat-supplies',
    categoryName: 'Insumos',
    categoryColor: '#EF9F27',
    date: daysAgo(8),
    provider: 'Proveedora del Valle',
    status: 'PAID',
  },
];

/** Simplified income movements for the mock finance dashboard (mirrors verified Payments). */
export const MOCK_INCOME_MOVEMENTS: FinanceMovement[] = [
  { id: 'inc1', type: 'income', concept: 'Pago — Roberto Gómez', amount: 3500, date: daysAgo(0), tag: 'TRANSFER', status: 'VERIFIED' },
  { id: 'inc2', type: 'income', concept: 'Pago — Ana López', amount: 2800, date: daysAgo(1), tag: 'CARD', status: 'VERIFIED' },
  { id: 'inc3', type: 'income', concept: 'Pago — Carlos Ruiz', amount: 3500, date: daysAgo(2), tag: 'CASH', status: 'VERIFIED' },
  { id: 'inc4', type: 'income', concept: 'Pago — María Torres', amount: 3500, date: daysAgo(4), tag: 'TRANSFER', status: 'VERIFIED' },
  { id: 'inc5', type: 'income', concept: 'Pago — Jorge Díaz', amount: 2800, date: daysAgo(6), tag: 'TRANSFER', status: 'VERIFIED' },
];

export const MOCK_DEVICES: AccessDevice[] = [
  {
    id: 'd1',
    name: 'Barrera Entrada Residentes',
    type: DeviceTypeEnum.VEHICLE_BARRIER,
    ipAddress: '192.168.1.101',
    location: 'Acceso Principal',
    status: DeviceStatusEnum.ONLINE,
  },
  {
    id: 'd2',
    name: 'Cámara LPR Entrada',
    type: DeviceTypeEnum.CAMERA_LPR,
    ipAddress: '192.168.1.102',
    location: 'Carril 1',
    status: DeviceStatusEnum.ONLINE,
  },
  {
    id: 'd3',
    name: 'Puerta Peatonal Lobby',
    type: DeviceTypeEnum.PEDESTRIAN_GATE,
    ipAddress: '192.168.1.105',
    location: 'Lobby Torre A',
    status: DeviceStatusEnum.OFFLINE,
  },
];

export const MOCK_PLATES: LicensePlate[] = [
  {
    id: 'lp1',
    number: 'ABC-987',
    alias: 'Coche Roberto',
    status: PlateStatusEnum.WHITELIST,
    addedAt: new Date().toISOString(),
  },
  {
    id: 'lp2',
    number: 'BAD-666',
    alias: 'Vecino Moroso',
    status: PlateStatusEnum.BLACKLIST,
    addedAt: new Date().toISOString(),
    notes: 'Bloqueado por falta de pago',
  },
];

export const MOCK_ANNOUNCEMENTS: Announcement[] = [
  {
    id: 'ann1',
    title: 'Mantenimiento Elevadores',
    content: 'Se realizará mantenimiento preventivo en la torre B mañana de 10am a 2pm.',
    type: AnnouncementTypeEnum.IMPORTANT,
    date: new Date().toISOString().split('T')[0],
    icon: 'construct-outline',
  },
  {
    id: 'ann2',
    title: 'Fumigación de Áreas Comunes',
    content: 'Se realizará fumigación el día lunes. Favor de cerrar ventanas.',
    type: AnnouncementTypeEnum.IMPORTANT,
    date: new Date(Date.now() - 86400000).toISOString().split('T')[0],
    icon: 'bug-outline',
  },
  {
    id: 'ann3',
    title: 'Fiesta de Fin de Año',
    content: 'Acompáñanos a celebrar. ¿Qué prefieren de comida?',
    type: AnnouncementTypeEnum.EVENT,
    date: new Date(Date.now() - 86400000 * 2).toISOString().split('T')[0],
    icon: 'balloon-outline',
    poll: {
      id: 'poll-ann-3',
      title: 'Opciones de Menú',
      description: 'Elige tu comida favorita',
      status: 'ACTIVE',
      allowMultiple: false,
      isAnonymous: false,
      startsAt: new Date().toISOString(),
      endsAt: new Date(Date.now() + 86400000).toISOString(),
      createdBy: 'admin-1',
      createdAt: new Date().toISOString(),
      question: 'Opciones de Menú',
      options: [
        { id: 'opt1', text: 'Tacos', votes: 15 },
        { id: 'opt2', text: 'Pizza', votes: 8 },
      ],
      hasVoted: true,
      userVotedOptionIds: ['opt1'],
    },
  },
  {
    id: 'ann4',
    title: 'Corte de Agua Programado',
    content: 'El próximo viernes habrá corte de agua de 8am a 12pm por mantenimiento de cisternas.',
    type: AnnouncementTypeEnum.URGENT,
    date: new Date(Date.now() - 86400000 * 3).toISOString().split('T')[0],
    icon: 'water-outline',
  },
];

export const MOCK_TICKETS: Ticket[] = [
  {
    id: 't1',
    userId: 'u1',
    title: 'Fuga en el baño',
    description: 'Hay una fuga constante en el baño de visitas.',
    category: TicketCategoryEnum.LEAK,
    status: TicketStatusEnum.NEW,
    date: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    comments: [],
  },
  {
    id: 't2',
    userId: 'u4',
    title: 'Lámpara fundida pasillo',
    description: 'Piso 1, frente al 101',
    category: TicketCategoryEnum.LIGHTING,
    status: TicketStatusEnum.RESOLVED,
    date: new Date(Date.now() - 10000000).toISOString(),
    updatedAt: new Date(Date.now() - 5000000).toISOString(),
    comments: [
      { id: 'c1', userId: 'u2', userName: 'Admin', text: 'Ya fue reemplazada.', date: new Date().toISOString(), isAdmin: true },
    ],
  },
];

export const MOCK_AUDIT_LOGS: SystemAuditLog[] = [
  {
    id: 'aud-1',
    timestamp: new Date(Date.now() - 100000).toISOString(),
    actorId: 'u2',
    actorName: 'Admin Principal',
    action: AuditActionEnum.UPDATE,
    resource: 'SystemSettings',
    details: 'Actualizó el límite de visitas a 15',
  },
  {
    id: 'aud-2',
    timestamp: new Date(Date.now() - 500000).toISOString(),
    actorId: 'u2',
    actorName: 'Admin Principal',
    action: AuditActionEnum.CREATE,
    resource: 'User',
    details: 'Creó el usuario Pedro Inquilino (u5)',
    resourceId: 'u5',
  },
  {
    id: 'aud-3',
    timestamp: new Date(Date.now() - 86400000).toISOString(),
    actorId: 'u3',
    actorName: 'Guardia Entrada',
    action: AuditActionEnum.LOGIN,
    resource: 'Auth',
    details: 'Inicio de sesión en caseta principal',
  },
];

export const MOCK_SETTINGS: SystemSettings = {
  id: 'sys-1',
  condoName: 'Residencial Real del Bosque',
  address: 'Av. Bosque Real 123, CDMX',
  contactEmail: 'admin@residencial.com',
  contactPhone: '55 1234 5678',
  emergencyPhone: '911',
  website: 'www.residencialreal.com',
  visitorLimitPerUnit: 15,
  defaultMaintenanceFee: 2500,
  currency: 'MXN',
  timezone: 'America/Mexico_City',
  branding: {
    appName: 'Hommi',
    primaryColor: '#8B5CF6',
    logoUrl: '',
  },
  bankInfo: {
    bankName: 'BBVA Bancomer',
    accountName: 'Condominio Real del Bosque AC',
    accountNumber: '0123456789',
    clabe: '012180001234567890',
  },
  fiscalData: {
    rfc: 'CRB101010ABC',
    businessName: 'Condominio Real del Bosque A.C.',
    taxRegime: '603 - Personas Morales con Fines no Lucrativos',
    zipCode: '11000',
    address: 'Av. Bosque Real 123, Col. Lomas, CDMX',
    email: 'facturacion@residencial.com',
  },
  integrations: {
    hikCentral: {
      serverUrl: 'https://hikcentral.residencial.com',
      apiKey: 'hk_v2_xxxx',
      enabled: true,
    },
    dahua: { enabled: false },
    zkteco: { enabled: false },
    stripe: {
      apiKey: 'pk_test_xxxx',
      publicKey: 'pk_test_public_xxxx',
      secretKey: 'sk_test_xxxx',
      enabled: true,
    },
    mercadopago: { enabled: false },
    conekta: { enabled: false },
    aws: {
      bucketName: 'resiguard-storage',
      region: 'us-east-1',
      accessKeyId: 'AKIAXXXX',
      secretAccessKey: 'xxxx',
      enabled: true,
    },
    smtp: {
      host: 'smtp.gmail.com',
      port: 587,
      user: 'notificaciones@resiguard.com',
      pass: '****',
      secure: true,
      enabled: true,
    },
    twilio: {
      accountSid: 'ACxxxx',
      authToken: 'xxxx',
      enabled: false,
    },
    whatsapp: {
      phoneNumberId: '',
      accessToken: '',
      businessAccountId: '',
      enabled: false,
    },
  },
};

export const MOCK_DOCUMENTS: CommunityDocument[] = [
  {
    id: 'doc1',
    title: 'Reglamento General de Condominio',
    category: DocumentCategoryEnum.BYLAWS,
    uploadDate: '2023-01-15T10:00:00Z',
    url: '#',
    size: '2.4 MB',
    uploadedBy: 'Admin',
  },
  {
    id: 'doc2',
    title: 'Acta de Asamblea Octubre 2023',
    category: DocumentCategoryEnum.MINUTES,
    uploadDate: '2023-10-20T18:30:00Z',
    url: '#',
    size: '1.1 MB',
    uploadedBy: 'Admin',
  },
  {
    id: 'doc3',
    title: 'Estado Financiero Q3 2023',
    category: DocumentCategoryEnum.FINANCIAL,
    uploadDate: '2023-10-05T09:00:00Z',
    url: '#',
    size: '800 KB',
    uploadedBy: 'Contador',
  },
  {
    id: 'doc4',
    title: 'Póliza de Seguro Áreas Comunes',
    category: DocumentCategoryEnum.INSURANCE,
    uploadDate: '2023-02-01T12:00:00Z',
    url: '#',
    size: '5.0 MB',
    uploadedBy: 'Admin',
  },
];
